import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, ChevronDown, ChevronRight, Check, Minus, Search, Save } from "lucide-react";

// Checkbox tri-estado (marcado / indeterminado / vacío). Es un <span> para poder
// anidarse dentro de botones de fila sin generar DOM inválido.
function TriCheck({ state, disabled, onClick, testid }) {
  return (
    <span
      role="checkbox"
      aria-checked={state === "on"}
      data-testid={testid}
      onClick={(e) => { if (!disabled && onClick) onClick(e); }}
      className={`h-5 w-5 rounded-[5px] border inline-flex items-center justify-center shrink-0 transition-colors ${
        state === "on" ? "bg-slate-900 border-slate-900 text-white"
        : state === "mixed" ? "bg-slate-900 border-slate-900 text-white"
        : "bg-white border-slate-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-slate-500 cursor-pointer"}`}
    >
      {state === "on" && <Check size={13} strokeWidth={3} />}
      {state === "mixed" && <Minus size={13} strokeWidth={3} />}
    </span>
  );
}

export default function RoleEditor() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [rol, setRol] = useState(null);
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [sel, setSel] = useState({ secciones: new Set(), fases: new Set(), multimedia: new Set(), acciones: new Set() });
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/permisos").then((r) => {
      setMeta(r.data);
      const found = (r.data.roles || []).find((x) => x.id === roleId);
      if (!found) { navigate("/roles"); return; }
      setRol(found);
      setNombre(found.nombre);
      setDescripcion(found.descripcion || "");
      setSel({
        secciones: new Set(found.secciones || []),
        fases: new Set(found.fases || []),
        multimedia: new Set(found.multimedia || []),
        acciones: new Set(Object.entries(found.acciones || {}).filter(([, v]) => v).map(([k]) => k)),
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  // Estructura de grupos del árbol de permisos, derivada del meta del backend.
  const groups = useMemo(() => {
    if (!meta) return [];
    return [
      { key: "acciones", label: "Acciones y permisos generales", items: meta.acciones },
      { key: "secciones", label: "Secciones de la ficha del modelo", items: meta.secciones },
      { key: "fases", label: "Compuertas por fase", items: meta.fases.map((f) => ({ key: f.key, label: f.label })) },
      { key: "multimedia", label: "Multimedia", items: meta.multimedia },
    ];
  }, [meta]);

  const esAdmin = rol?.es_admin;
  const bloqueado = esAdmin; // el rol admin siempre tiene todo

  const filtrar = (items) => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((i) => i.label.toLowerCase().includes(t));
  };

  const isOn = (grupo, key) => esAdmin || sel[grupo].has(key);

  const toggleLeaf = (grupo, key) => {
    if (bloqueado) return;
    setSel((prev) => {
      const next = new Set(prev[grupo]);
      next.has(key) ? next.delete(key) : next.add(key);
      return { ...prev, [grupo]: next };
    });
  };

  const groupState = (g) => {
    const keys = g.items.map((i) => i.key);
    const on = keys.filter((k) => isOn(g.key, k)).length;
    if (on === 0) return "off";
    if (on === keys.length) return "on";
    return "mixed";
  };

  const toggleGroup = (g) => {
    if (bloqueado) return;
    const st = groupState(g);
    setSel((prev) => {
      const next = new Set(prev[g.key]);
      if (st === "on") g.items.forEach((i) => next.delete(i.key));
      else g.items.forEach((i) => next.add(i.key));
      return { ...prev, [g.key]: next };
    });
  };

  const allState = () => {
    const states = groups.map((g) => groupState(g));
    if (states.every((s) => s === "on")) return "on";
    if (states.every((s) => s === "off")) return "off";
    return "mixed";
  };

  const toggleAll = () => {
    if (bloqueado) return;
    const st = allState();
    setSel(() => {
      const empty = { secciones: new Set(), fases: new Set(), multimedia: new Set(), acciones: new Set() };
      if (st === "on") return empty;
      groups.forEach((g) => g.items.forEach((i) => empty[g.key].add(i.key)));
      return empty;
    });
  };

  const expandAll = () => {
    const all = {};
    groups.forEach((g) => { all[g.key] = true; });
    const anyClosed = groups.some((g) => !expanded[g.key]);
    setExpanded(anyClosed ? all : {});
  };

  const guardar = async () => {
    setBusy(true);
    try {
      const acciones = {};
      (meta.acciones || []).forEach((a) => { acciones[a.key] = sel.acciones.has(a.key); });
      const { data } = await api.put(`/roles/${roleId}`, {
        nombre,
        descripcion,
        secciones: [...sel.secciones],
        fases: [...sel.fases],
        multimedia: [...sel.multimedia],
        acciones,
      });
      const found = data.roles.find((x) => x.id === roleId);
      setRol(found);
      toast.success("Rol guardado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (user && !user.es_admin) return <Navigate to="/" replace />;
  if (!meta || !rol) return <div className="p-6 lg:p-8 max-w-3xl"><Skeleton className="h-96 rounded-xl" /></div>;

  const totalSel = groups.reduce((acc, g) => acc + g.items.filter((i) => isOn(g.key, i.key)).length, 0);
  const totalAll = groups.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <div className="p-6 lg:p-8 max-w-3xl pb-16">
      <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-4">
        <Link to="/roles" className="inline-flex items-center gap-1 hover:text-slate-800 transition-colors"><ChevronLeft size={15} /> Roles</Link>
        <span className="text-slate-300">/</span>
        <span className="font-medium text-slate-800">{rol.nombre}</span>
      </div>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">{rol.nombre}</h1>
        <Button onClick={guardar} disabled={busy} className="rounded-lg" data-testid="save-role-button">
          <Save size={16} className="mr-1.5" /> Guardar
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm p-5 space-y-4 mb-6">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label>Nombre</Label>
            <span className="text-xs text-slate-400 tabular-nums">{nombre.length}/255</span>
          </div>
          <Input value={nombre} maxLength={255} onChange={(e) => setNombre(e.target.value)} data-testid="role-nombre-input" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label>Descripción</Label>
            <span className="text-xs text-slate-400 tabular-nums">{descripcion.length}/255</span>
          </div>
          <Textarea rows={2} value={descripcion} maxLength={255} onChange={(e) => setDescripcion(e.target.value)} data-testid="role-descripcion-input" />
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-head font-bold tracking-tight">Permisos</h3>
            <p className="text-xs text-slate-400 mt-0.5">{esAdmin ? "El Administrador siempre tiene acceso total." : `${totalSel} de ${totalAll} permisos seleccionados.`}</p>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar" className="pl-9 h-9 bg-slate-50" data-testid="perm-search" />
          </div>

          <div className="flex items-center justify-between">
            <button type="button" onClick={toggleAll} disabled={bloqueado} className={`flex items-center gap-2.5 text-sm font-medium text-slate-700 ${bloqueado ? "opacity-50 cursor-not-allowed" : ""}`} data-testid="select-all-perms">
              <TriCheck state={esAdmin ? "on" : allState()} disabled={bloqueado} onClick={() => {}} />
              Seleccionar todos los permisos
            </button>
            <button type="button" onClick={expandAll} className="text-sm text-blue-600 hover:text-blue-700" data-testid="expand-all">Expandir todo</button>
          </div>

          <div className="border-t border-slate-100">
            {groups.map((g) => {
              const items = filtrar(g.items);
              if (items.length === 0) return null;
              const abierto = !!expanded[g.key] || !!q.trim();
              const st = groupState(g);
              const onCount = g.items.filter((i) => isOn(g.key, i.key)).length;
              return (
                <div key={g.key} className="border-b border-slate-100" data-testid={`perm-group-${g.key}`}>
                  <div className="flex items-center gap-3 py-3">
                    <TriCheck state={esAdmin ? "on" : st} disabled={bloqueado} onClick={() => toggleGroup(g)} testid={`group-check-${g.key}`} />
                    <button type="button" onClick={() => setExpanded((p) => ({ ...p, [g.key]: !p[g.key] }))} className="flex-1 flex items-center justify-between text-left" data-testid={`group-toggle-${g.key}`}>
                      <span className="text-sm font-medium text-slate-700">{g.label}</span>
                      <span className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 tabular-nums">{onCount}/{g.items.length}</span>
                        {abierto ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                      </span>
                    </button>
                  </div>
                  {abierto && (
                    <div className="pb-2 pl-8 space-y-2">
                      {items.map((i) => (
                        <button
                          key={i.key}
                          type="button"
                          onClick={() => toggleLeaf(g.key, i.key)}
                          disabled={bloqueado}
                          className={`w-full flex items-center gap-3 text-left ${bloqueado ? "opacity-60 cursor-not-allowed" : ""}`}
                          data-testid={`perm-${g.key}-${i.key}`}
                        >
                          <TriCheck state={isOn(g.key, i.key) ? "on" : "off"} disabled={bloqueado} onClick={() => {}} />
                          <span className="text-sm text-slate-600">{i.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
}
